import { memo, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { RoutePath } from 'consts/RoutePath';
import { Hr } from 'components/atoms/Hr';
import { ApiError } from 'requests/RequestBase';
import { UserWithGroupAuth, callUpdateGroupUserApi, UpdateGroupUserReqData } from 'requests/RequestGroup';
import { callGetServiceProhibitionApi, GetServiceProhibitionResData } from 'requests/RequestUser';
import { findGroupAuthByKey, isAdmin, isUser, isMember, GroupAuth } from 'consts/GroupAuthConst';
import { findSexTypeNameByKey } from 'consts/SexConst';
import classes from './override.module.scss';
import { HttpStatus } from 'consts/HttpStatus';
import { CircularProgress, Backdrop } from '@material-ui/core';
import { BirthDay } from 'components/molecules/BirthDay';

type Props = {};
type MemberInfoDto = {
	groupId: string;
	targetUser: UserWithGroupAuth;
	loginUser: UserWithGroupAuth;
};

export const MemberInfoTemplate = memo((_props: Props) => {
	const history = useHistory<MemberInfoDto>();
	const dto = history.location.state;
	const targetUserAuth = findGroupAuthByKey(dto.targetUser);
	const loginUserAuth = findGroupAuthByKey(dto.loginUser);
	const [hasChangePaymentPermit, setHasChangePaymentPermit] = useState(dto.targetUser.payment_not_flg === 0);
	const [hasMemberEditPermit, setHasMemberEditPermit] = useState(dto.targetUser.member_updatable_flg === 1);

	const [changePaymentPermitLoading, setChangePaymentPermitLoading] = useState(false);
	const changePaymentPermit = async () => {
		setChangePaymentPermitLoading(true);
		// 決済機能利用許可変更API
		const updateGroupUserReqData: UpdateGroupUserReqData = {
			gidpk: dto.targetUser.gidpk,
			payment_not_flg: hasChangePaymentPermit ? 1 : 0, // 権限あり(0)の場合はなし(1)に、なし(1)の場合はあり(0)にする
			member_updatable_flg: dto.targetUser.member_updatable_flg,
		};
		const result = await callUpdateGroupUserApi(dto.groupId, updateGroupUserReqData);
		if (result.status !== HttpStatus.OK) {
			setChangePaymentPermitLoading(false);
			history.push(RoutePath.ERROR);
			return;
		}

		dto.targetUser.payment_not_flg = hasChangePaymentPermit ? 1 : 0;
		setHasChangePaymentPermit((prev) => !prev);
		setChangePaymentPermitLoading(false);
	};
	const getPaymentPermitButtonLabel = () => {
		return hasChangePaymentPermit ? `制限する` : `制限しない`;
	};

	const [changeMemberEditPermitLoading, setChangeMemberEditPermitLoading] = useState(false);
	const changeMemberEditPermit = async () => {
		setChangeMemberEditPermitLoading(true);
		// こどもID編集権限変更API
		const updateGroupUserReqData: UpdateGroupUserReqData = {
			gidpk: dto.targetUser.gidpk,
			payment_not_flg: dto.targetUser.payment_not_flg,
			member_updatable_flg: hasMemberEditPermit ? 0 : 1, // 権限あり(true)の場合はなし(0)に、なし(false)の場合はあり(1)にする
		};
		const result = await callUpdateGroupUserApi(dto.groupId, updateGroupUserReqData);
		if (result.status !== HttpStatus.OK) {
			setChangeMemberEditPermitLoading(false);
			history.push(RoutePath.ERROR);
			return;
		}

		dto.targetUser.member_updatable_flg = hasMemberEditPermit ? 0 : 1;
		setHasMemberEditPermit((prev) => !prev);
		setChangeMemberEditPermitLoading(false);
	};
	const getMemberEditPermitButtonLabel = () => {
		return hasMemberEditPermit ? `編集不可にする` : `編集可能にする`;
	};

	const [serviceProhibitionData, setServiceProhibition] = useState<GetServiceProhibitionResData>(null);
	const [isGetDataLoading, setIsGetDataLoading] = useState(false);
	const scrollRef = useRef(null);
	useEffect(() => {
		const execute = async () => {
			setIsGetDataLoading(true);
			const prohibitionResult = await callGetServiceProhibitionApi({ gidpk: dto.targetUser.gidpk });
			if (prohibitionResult.status !== HttpStatus.OK) {
				const errorData = prohibitionResult.data as ApiError;
				console.log(errorData.message);
				history.push(RoutePath.ERROR);
				setIsGetDataLoading(false);
				return;
			}
			const serviceProhibitionDataResData = prohibitionResult.data as GetServiceProhibitionResData;
			setServiceProhibition(serviceProhibitionDataResData);
			setIsGetDataLoading(false);
		};
		execute();

		scrollRef.current.scrollIntoView();
		document.title = 'ユーザー情報';
	}, []);
	const isUseProhibitedService = !!serviceProhibitionData?.use_prohibited_service;
	const menber_remove_path = isUseProhibitedService ? RoutePath.MEMBER_REMOVE_CONTACT_FORM : RoutePath.MEMBER_REMOVE;

	return (
		<>
			<Backdrop className={classes.backdrop} open={isGetDataLoading}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<section className="section mypage" ref={scrollRef}>
				<div className="breadcrumb">
					<ul>
						<li>
							<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
						</li>
						<li>
							<Link to={RoutePath.FAMILY_INFO}>家族情報</Link>
						</li>
						<li>ユーザー情報</li>
					</ul>
				</div>
				<h1 className="page-head">ユーザー情報</h1>
				<div className="section-in">
					<div className={classes.sectionHead}>
						<img
							className={classes.memberInfoIcon}
							src={`${process.env.PUBLIC_URL}/images/${targetUserAuth.image}`}
							alt=""
						/>
						<span className={classes.sectionHeadH1}>
							{isMember(targetUserAuth) ? `Gakken${targetUserAuth.name}` : targetUserAuth.name}
						</span>
					</div>
					<div className="info-box">
						<div className="info-head">GakkenID</div>
						<div className="info-txt">{dto.targetUser.gid}</div>
						<Hr />
						{!isMember(targetUserAuth) && (
							<>
								<div className="info-head">メールアドレス</div>
								<div className="info-txt">{dto.targetUser.mailaddress_1}</div>
								<Hr />
							</>
						)}
						<div className="info-head">お名前（全角）</div>
						<div className="info-txt">{`${dto.targetUser.lastname}　${dto.targetUser.firstname}`}</div>
						<Hr />
						<div className="info-head">お名前（カナ）</div>
						<div className="info-txt">{`${dto.targetUser.kana_lastname}　${dto.targetUser.kana_firstname}`}</div>
						<Hr />
						<div className="info-head">生年月日</div>
						<div className="info-txt">
							<BirthDay birth={dto.targetUser.birth} isFlexStart={true} />
						</div>
						<Hr />
						<div className="info-head">性別</div>
						<div className="info-txt">{findSexTypeNameByKey(dto.targetUser.sex) || '　'}</div>
						<div className="btn-box">
							{/* 代表者情報を開いている */}
							{isAdmin(targetUserAuth) && (
								<>
									<div className="btn gr">
										<Link to={RoutePath.MY_PAGE}>
											<span>マイページTOPへ戻る</span>
										</Link>
									</div>
								</>
							)}
							{/* 一般ユーザー情報を開いている */}
							{isUser(targetUserAuth) && (
								<>
									<div className="btn wh">
										<Link
											to={{
												pathname: RoutePath.GET_OUT_FAMILY,
												state: dto,
											}}
										>
											<span>「家族」からはずす</span>
										</Link>
									</div>
									<div className="btn gr">
										<Link to={RoutePath.MY_PAGE}>
											<span>マイページTOPへ戻る</span>
										</Link>
									</div>
								</>
							)}
							{/* メンバー情報を開いている&ログインユーザにメンバー編集権限がある */}
							{isMember(targetUserAuth) && dto.loginUser.member_updatable_flg === 1 && (
								<>
									<div className="btn wh">
										<Link
											to={{
												pathname: menber_remove_path,
												state: dto,
											}}
										>
											<span>削除</span>
										</Link>
									</div>
									<div className="btn gr">
										<Link to={{ pathname: RoutePath.MEMBER_UPDATE, state: dto }}>
											<span>変更</span>
										</Link>
									</div>
								</>
							)}
						</div>
					</div>
				</div>

				{/* メンバー情報を開いている&ログインユーザにメンバー編集権限がある */}
				{/* Ph3以降対応のためコメントアウト */}
				{/* {isMember(targetUserAuth) && dto.loginUser.member_updatable_flg === 1 && (
					<div className={classes.memberInfoChgId}>
						<div className="btn-box">
							<div className="btn gr">
								<Link to={RoutePath.MY_PAGE}>
									<span>GakkenIDに変更</span>
								</Link>
							</div>
						</div>
						<div
							className={classes.memberInfoChgIdDiv}
						>{`Gakken${GroupAuth.MEMBER.name}を「GakkenID」に変更するにはメールアドレスが必須となります。`}</div>
						<div className={classes.memberInfoChgIdDiv2}>
							※変更するとサービス利用時に決済が可能になるなど、一部機能の制限がなくなります
						</div>
						<div
							className={classes.memberInfoChgIdDiv}
						>{`「GakkenID」と「Gakken${GroupAuth.MEMBER.name}」の違いについてはこちら`}</div>
					</div>
				)} */}
				{/* 一般ユーザー情報を開いている */}
				{isUser(targetUserAuth) && (
					<>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span className={classes.sectionHeadH1}>権限管理</span>
							</div>
							<div className="section-head">
								<span>商品購入機能の利用制限</span>
							</div>
							<div className="info-box">
								<div>Gakken Mall、Gakken Goods Galleryでの商品購入の利用制限について変更ができます。</div>
								<div>
									現在のステータスは<b>{hasChangePaymentPermit ? `「制限しない」` : `「制限する」`}</b>
									となります。{isAdmin(loginUserAuth) ? `機能を変更する場合はタップをしてください。` : ''}
								</div>
								{isAdmin(loginUserAuth) ? (
									<div className="btn-box">
										<div className="btn wh">
											{changePaymentPermitLoading ? (
												<CircularProgress />
											) : (
												<button onClick={changePaymentPermit}>{getPaymentPermitButtonLabel()}</button>
											)}
										</div>
									</div>
								) : (
									<>
										<div>※権限の変更は代表者のみ可能です</div>
										<Hr />
										<p className={classes.permissionLabel}>{getPaymentPermitButtonLabel()}</p>
										<Hr />
									</>
								)}
							</div>
							{isAdmin(loginUserAuth) && <Hr />}
							<div className={`${classes.marginTop35} section-head`}>
								<span>{`Gakken${GroupAuth.MEMBER.name}の編集権限`}</span>
							</div>
							<div className="info-box">
								<div>{`Gakken${GroupAuth.MEMBER.name}の新規登録やプロフィール情報の変更など、編集権限の変更ができます。`}</div>
								<div>
									現在のステータスは<b>{hasMemberEditPermit ? `「編集可能」` : `「編集不可」`}</b>
									となります。{isAdmin(loginUserAuth) ? `機能を変更する場合はタップをしてください。` : ''}
								</div>
								{isAdmin(loginUserAuth) ? (
									<div className="btn-box">
										<div className="btn wh">
											{changeMemberEditPermitLoading ? (
												<CircularProgress />
											) : (
												<button onClick={changeMemberEditPermit}>{getMemberEditPermitButtonLabel()}</button>
											)}
										</div>
									</div>
								) : (
									<>
										<div>※権限の変更は代表者のみ可能です</div>
										<Hr />
										<p className={classes.permissionLabel}>{getMemberEditPermitButtonLabel()}</p>
										<Hr />
									</>
								)}
							</div>
						</div>
					</>
				)}
			</section>
		</>
	);
});
